import { useEffect, useRef } from "react";
import api from "@/lib/api";

export const trackEvent = (event_type: string, metadata: Record<string, unknown>) =>
  api.post("/analytics/track", { event_type, metadata }).catch(() => {});

export const useArticleAnalytics = (slug?: string) => {
  const fired = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!slug) return;
    let acc = 0;
    let from = document.visibilityState === "visible" ? Date.now() : 0;
    let sent = false;
    const flush = () => {
      if (from) { acc += Date.now() - from; from = 0; }
      const seconds = Math.round(acc / 1000);
      if (sent || seconds < 5) return;
      sent = true;
      try {
        navigator.sendBeacon(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/analytics/track`,
          new Blob([JSON.stringify({ event_type: "article_engaged", metadata: { slug, seconds } })],
            { type: "application/json" }));
      } catch {}
    };
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        if (from) { acc += Date.now() - from; from = 0; }
      } else if (!from) {
        from = Date.now();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    fired.current = new Set();
    const milestones = [25, 50, 75, 90];
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const el = document.querySelector('[data-testid="article-page"] article');
        if (!el) return;
        const end = el.getBoundingClientRect().bottom + window.scrollY - window.innerHeight;
        const p = end > 0 ? (window.scrollY / end) * 100 : 0;
        for (const m of milestones) {
          const key = `scroll_${m}`;
          if (p >= m && !fired.current.has(key)) {
            fired.current.add(key);
            trackEvent("article_scroll", { slug, depth: m });
          }
        }
      });
    };
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const name = (e.target as HTMLElement).dataset.track;
        if (name && !fired.current.has(name)) {
          fired.current.add(name);
          trackEvent("article_module_view", { slug, module: name });
          io.unobserve(e.target);
        }
      }
    }, { threshold: 0.4 });
    const t = setTimeout(() => {
      document.querySelectorAll("[data-track]").forEach((n) => io.observe(n));
    }, 1500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(t);
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [slug]);
};
